// src/pages/SimuladorDecision.jsx
// Ruta: /recursos/simulador-decision
// Simulador de Decisión Ejecutiva — Decision Lab · Beta
// 3 rondas · 18 perfiles · mobile-first

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

/* ─── PALETA ────────────────────────────────────────────────────── */
const C = {
  navy:     '#0D1B2A',
  navyMid:  '#1A2D42',
  navyLt:   '#243447',
  teal:     '#2ABFBF',
  tealDk:   '#1E9494',
  tealPale: 'rgba(42,191,191,0.12)',
  red:      '#E8344E',
  redPale:  'rgba(232,52,78,0.12)',
  white:    '#F0F4F8',
  muted:    '#8AA0B8',
  body:     '#C8D8E8',
  mid:      '#B0C4D8',
  border:   'rgba(42,191,191,0.2)',
};

/* ─── DATA ──────────────────────────────────────────────────────── */
const FEEDBACK = {
  r1: {
    A: {
      tag: 'Sistema 1 — Dominó', tagRed: true, system: 'S1',
      title: 'El argumento del momentum es real. Y también es la coartada favorita del Sistema 1.',
      body: 'Kahneman describe el Sistema 1 como instantáneo, sin esfuerzo, y orientado a narrativas cómodas. El historial de Valentina, la fluidez del pitch y el consenso del comité construyeron exactamente esa narrativa. El argumento que usaste para aprobar —"tiene el historial para respaldarlo"— no evalúa esta propuesta: evalúa la anterior. El Sistema 1 transfiere credibilidad entre contextos sin pedir permiso. Los supuestos de ROI sin benchmark y el plan de adopción sobredimensionado no desaparecen porque Valentina haya cumplido antes.',
      q: '¿Cuándo fue la última vez que frenaste una propuesta de alguien con buen historial porque los números no cerraban?',
    },
    B: {
      tag: 'Sistema 2 — Activado con fricción', tagRed: false, system: 'S2partial',
      title: 'Separaste el avance del compromiso. El Sistema 2 entró justo antes de firmar.',
      body: 'La corriente del escenario empujaba hacia la aprobación total: historial sólido, narrativa fluida, comité ya alineado. Aprobar solo la fase de diseño sin liberar el presupuesto completo introduce una fricción deliberada — exactamente el mecanismo que Kahneman describe para contrarrestar el Sistema 1. No lo apagaste: lo diseñaste para que no decidiera solo. Eso es más sofisticado que el rechazo directo, porque mantiene el momentum sin transferir el riesgo.',
      q: '¿Usas este mecanismo de forma sistemática o fue intuitivo en este caso?',
    },
    C: {
      tag: 'Sistema 2 — Dominó', tagRed: false, system: 'S2',
      title: 'Frenaste la corriente cuando todos ya habían asentido.',
      body: 'El Sistema 2 es deliberado, lento, y se activa con fricción — por eso domina por defecto solo cuando hay un proceso que lo fuerza. En este escenario, tres fuerzas empujaban hacia la aprobación: el historial de Valentina, la narrativa fluida del pitch, y el consenso previo del comité. Resistir las tres requiere separar deliberadamente la calidad del presentador de la calidad de los supuestos. Lo que pediste —revisión de ROI y plan de adopción— es el mínimo para que una propuesta sea evaluable, independientemente de quién la presente.',
      q: '¿Qué tan frecuente es que el último en votar en tu organización cambie el resultado?',
    },
  },
  r2: {
    A: {
      tag: 'Nivel Evento — Intervención táctica', tagRed: true, level: 'Evento',
      title: 'Optimizaste la respuesta al síntoma. El sistema que lo produce sigue intacto.',
      body: 'Meadows describe el nivel del evento como la superficie visible del sistema: lo que ocurre, no por qué ocurre. Intervenir aquí tiene lógica real — el equipo sabe ejecutarlo, el resultado es medible, y el pico de este mes queda cubierto. El problema es que resolver el evento con tanta eficiencia elimina la urgencia de preguntarse por la causa. Tres trimestres consecutivos con el mismo patrón indican que algo en la estructura lo produce. Optimizar la respuesta lo hace más manejable, pero no lo detiene.',
      q: '¿Cuántas "mejoras" de tu equipo este año son variaciones de la misma respuesta a un patrón que no cambia?',
    },
    B: {
      tag: 'Nivel Patrón — Intervención comportamental', tagRed: false, level: 'Patrón',
      title: 'Atacaste cómo el sistema responde. No qué lo produce.',
      body: 'El nivel del patrón, según Meadows, es donde los datos muestran tendencias si los miras. Rediseñar el protocolo opera aquí: actúa sobre el comportamiento del sistema, no solo sobre el evento puntual. Eso tiene valor real — especialmente cuando el equipo está cansado del ciclo y el costo fijo no puede crecer. Pero si la causa estructural está en cómo se factura, en cómo se comunican cambios de condiciones, o en un proceso upstream que genera confusión cada inicio de mes, el protocolo mejorado convive con el problema.',
      q: '¿Qué proceso de tu operación mejoró significativamente pero sigue fallando en el mismo punto?',
    },
    C: {
      tag: 'Nivel Estructura — Intervención causal', tagRed: false, level: 'Estructura',
      title: 'La única intervención que rompe el ciclo. Con el costo más alto.',
      body: 'Meadows dice que la solución real está en la estructura: las reglas, los incentivos, los flujos de información que generan los patrones que producen los eventos. Elegir este nivel requiere tolerar que el equipo enfrente el pico de este mes sin solución nueva, mientras la investigación avanza. Eso tiene un costo político real — "vamos a investigar" no se siente como liderazgo cuando la crisis ya comenzó. Si el diagnóstico revela la causa estructural, el ciclo se rompe. Si no se sostiene, se convierte en otra demora antes del mismo parche.',
      q: '¿Tu organización tiene mecanismos para sostener un diagnóstico estructural cuando la presión operativa empuja hacia la acción inmediata?',
    },
  },
};

const PROFILES = {
  'S1-Evento-ruido':                { name: 'Decisor de superficie reactivo',             desc: 'El Sistema 1 dominó tu evaluación, intervienes en el nivel del evento, y tu criterio varía según el momento. No es incompetencia — es el patrón más común en organizaciones que operan bajo presión constante sin fricción deliberada en sus procesos de decisión. El riesgo no es que decidas mal: es que el sistema decide por ti y cada contexto produce un resultado diferente.', action: 'Esta semana, antes de aprobar cualquier propuesta, escribe una línea: ¿qué estoy evaluando exactamente — la persona o el proyecto? Si no puedes responderlo en 10 segundos, el Sistema 1 ya decidió por ti.' },
  'S1-Evento-consistente':          { name: 'Decisor táctico predecible',                 desc: 'El Sistema 1 dominó tu evaluación y operaste en el nivel del evento — pero tu criterio es consistente independientemente del contexto. Eso tiene valor: tu equipo puede anticipar cómo decides. El punto ciego es la profundidad, no la consistencia. Resuelves el síntoma de forma predecible, pero el patrón que lo genera sigue sin diagnóstico.', action: 'Esta semana, elige un problema recurrente de tu equipo y hazte una sola pregunta: ¿cuántos trimestres lleva apareciendo? Si la respuesta es más de dos, tienes un patrón — no un evento. Anótalo.' },
  'S1-Patrón-ruido':                { name: 'Decisor con criterio intermitente',          desc: 'Alcanzaste el nivel del patrón en tu intervención — eso ya implica una mirada más amplia que la mayoría. Pero el Sistema 1 influyó en tu evaluación inicial y tu criterio varía según el contexto. La combinación sugiere que tu profundidad de análisis depende de condiciones que no siempre controlas: energía, tiempo, presión social.', action: 'Esta semana, identifica en qué condiciones tomas tus decisiones más importantes. ¿Cuántas ocurren después de las 5pm o en días de alta carga? Pon una regla simple: decisiones de más de X monto o impacto no se toman en esas condiciones.' },
  'S1-Patrón-consistente':          { name: 'Decisor comportamental estable',             desc: 'Intervienes en el nivel del patrón y tu criterio es consistente — eso es más sofisticado que la mayoría. El Sistema 1 influyó en tu evaluación de personas, pero tu lectura sistémica de problemas organizacionales es sólida. El desarrollo pendiente está en separar la calidad de quien presenta de la calidad de lo que presenta.', action: 'Esta semana, antes de tu próxima reunión de aprobación, lee la propuesta sin saber quién la presenta. Si no es posible, escribe tres preguntas sobre los supuestos antes de escuchar el pitch. Las preguntas deben venir del documento, no del presentador.' },
  'S1-Estructura-ruido':            { name: 'Analista sistémico con ruido de ejecución', desc: 'Tu lectura de los problemas llega al nivel estructural — eso es poco común. Pero el Sistema 1 influyó en tu evaluación inicial y tu criterio varía según el contexto en que decides. La tensión es significativa: ves la causa raíz pero puedes aprobar o rechazar la solución dependiendo del día. El riesgo no está en tu análisis — está en las condiciones en que ejecutas.', action: 'Esta semana, define una regla personal de higiene decisional: qué condiciones deben cumplirse para que tomes una decisión de alto impacto. Escríbela. Si no puedes cumplirlas en el momento, difiere la decisión explícitamente.' },
  'S1-Estructura-consistente':      { name: 'Pensador sistémico con punto ciego social',  desc: 'Llegas al nivel estructural en tus intervenciones y tu criterio es consistente bajo presión contextual. El Sistema 1 te capturó en la evaluación de personas — el halo de Valentina funcionó. Eso sugiere que tu pensamiento sistémico es sólido cuando analizas problemas, pero se debilita cuando la presión social entra en juego.', action: 'Esta semana, en tu próxima reunión de aprobación grupal, vota por escrito antes de escuchar al resto del comité. Guarda tu voto. Compáralo con tu decisión final. La diferencia es el costo del consenso sobre tu criterio.' },
  'S2partial-Evento-ruido':         { name: 'Decisor cauteloso con exposición al contexto', desc: 'Introdujiste fricción deliberada antes de comprometer recursos — eso activa el Sistema 2. Pero operaste en el nivel del evento y tu criterio varía según el momento. Sabes frenar antes de firmar, pero la profundidad de tu intervención y la consistencia de tu juicio dependen de condiciones que fluctúan.', action: 'Esta semana, cuando sientas el impulso de resolver un problema rápido, añade una pregunta antes de actuar: ¿esto resuelve el evento o el patrón que lo produce? Si no sabes la respuesta, eso ya es información.' },
  'S2partial-Evento-consistente':   { name: 'Decisor cauteloso y predecible',             desc: 'Introduces fricción antes de comprometer recursos y eres consistente independientemente del contexto. El desarrollo pendiente está en la profundidad de intervención: operas en el nivel del evento con buena gobernanza, pero el patrón que genera los problemas sigue sin diagnóstico estructural.', action: 'Esta semana, mapea un problema recurrente de tu área en tres niveles: el evento visible, el patrón de comportamiento detrás, y la estructura que lo produce. No tienes que resolver nada todavía — solo ver los tres niveles juntos.' },
  'S2partial-Patrón-ruido':         { name: 'Decisor intermedio con criterio variable',   desc: 'Alcanzaste el nivel del patrón e introdujiste fricción deliberada en tu evaluación. Pero tu criterio varía según el contexto en que decides. Tienes las herramientas conceptuales — el ruido sugiere que su activación depende de condiciones que no siempre controlas. El trabajo está en hacer el proceso explícito para que no dependa del estado del momento.', action: 'Esta semana, diseña una checklist de dos preguntas que uses antes de cualquier decisión importante, independientemente de cómo te sientas. Dos preguntas — no más. El objetivo es que el proceso no dependa de tu estado.' },
  'S2partial-Patrón-consistente':   { name: 'Decisor con criterio metodológico en desarrollo', desc: 'Introdujiste fricción deliberada en tu evaluación, operaste en el nivel del patrón, y tu criterio es consistente bajo presión contextual. Las tres dimensiones muestran un perfil en construcción deliberada. El paso siguiente es llevar la intervención al nivel estructural sin perder la consistencia que ya tienes.', action: 'Esta semana, en el problema recurrente más importante de tu área, pregunta: ¿qué regla, incentivo o flujo de información produce este patrón? Si nadie en tu equipo puede responderlo, esa es la investigación que falta.' },
  'S2partial-Estructura-ruido':     { name: 'Analista riguroso con ruido contextual',     desc: 'Tu lectura llega al nivel estructural y no aprobaste sin fricción deliberada. Pero tu criterio varía según el contexto. La combinación sugiere que tu capacidad analítica es real, pero su activación no es sistemática — depende de condiciones que fluctúan. El desafío es institucionalizar lo que ya haces bien para que no dependa del día.', action: 'Esta semana, documenta cómo tomaste tu última decisión importante: qué preguntas te hiciste, en qué condiciones decidiste, qué información descartaste. El objetivo es convertir lo que haces intuitivamente en un proceso que puedas repetir — y enseñar.' },
  'S2partial-Estructura-consistente': { name: 'Decisor con arquitectura deliberada',      desc: 'Introdujiste fricción antes de comprometer recursos, tu intervención llegó al nivel estructural, y tu criterio se mantuvo consistente independientemente del contexto. Las tres dimensiones apuntan en la misma dirección: tienes un sistema de decisión, no solo criterio. La pregunta relevante es si ese sistema es explícito y transferible a tu equipo — o si vive solo en ti.', action: 'Esta semana, enseña tu proceso de decisión a alguien de tu equipo. No el resultado — el proceso. Si no puedes explicarlo en menos de 5 minutos, todavía no está suficientemente explícito.' },
  'S2-Evento-ruido':                { name: 'Evaluador riguroso con intervención superficial', desc: 'Resististe el Sistema 1 en la evaluación de personas — eso requiere fricción deliberada. Pero operaste en el nivel del evento y tu criterio varía según el contexto. La asimetría es interesante: tienes escrutinio para evaluar propuestas pero operas en superficie cuando intervenes en problemas organizacionales.', action: 'Esta semana, aplica el mismo rigor que usas para evaluar propuestas a un problema recurrente de tu equipo. Hazte la misma pregunta que harías a un presentador: ¿cuáles son los supuestos detrás de esta solución?' },
  'S2-Evento-consistente':          { name: 'Evaluador riguroso y predecible',            desc: 'Separaste la calidad del presentador de la calidad de la propuesta y tu criterio es consistente. El desarrollo pendiente está en la profundidad de intervención sistémica: tu rigor evaluativo no se tradujo todavía en una búsqueda de causas estructurales cuando el problema es recurrente.', action: 'Esta semana, cuando tu equipo te traiga un problema, antes de proponer soluciones pregunta: ¿cuántas veces hemos resuelto esto antes? Si la respuesta es más de una, la solución correcta no está en el nivel donde está el problema.' },
  'S2-Patrón-ruido':                { name: 'Decisor con profundidad variable',           desc: 'Resististe el Sistema 1 y alcanzaste el nivel del patrón — ambas son capacidades que requieren entrenamiento. Pero tu criterio varía según el contexto en que decides. El ruido es el punto ciego más relevante: puedes tener todo el criterio y aun así producir decisiones inconsistentes si el contexto no está controlado.', action: 'Esta semana, antes de decidir algo importante, escribe en una línea tu decisión y las condiciones en que la tomaste. Hora, nivel de energía, presiones activas. En 30 días, revisa si tus decisiones en buenas condiciones difieren de las tomadas bajo carga.' },
  'S2-Patrón-consistente':          { name: 'Decisor con criterio sólido en dos dimensiones', desc: 'Evaluaste la propuesta sin dejarte capturar por el halo, operaste en el nivel del patrón, y tu criterio es consistente. El paso siguiente está en la profundidad: llevar la intervención al nivel estructural cuando el patrón lleva tres trimestres repitiéndose es la diferencia entre mejorar el sistema y transformarlo.', action: 'Esta semana, identifica el patrón más costoso de tu organización y hazte una sola pregunta: ¿qué tendría que cambiar en la estructura para que este patrón dejara de producirse? No la solución — la condición estructural que lo genera.' },
  'S2-Estructura-ruido':            { name: 'Analista sistémico con exposición al ruido', desc: 'Resististe el Sistema 1 y llegaste al nivel estructural — las dos capacidades más exigentes del simulador. Pero tu criterio varía según el contexto. La combinación es significativa: tienes el análisis correcto pero puedes llegar a decisiones distintas con la misma información dependiendo del momento. Ahí está el trabajo.', action: 'Esta semana, define explícitamente qué condiciones deben existir para que tomes una decisión de alto impacto. Escríbelas como reglas, no como intenciones. Si las condiciones no se cumplen, la decisión se difiere — sin excepción.' },
  'S2-Estructura-consistente':      { name: 'Decisor con criterio estructurado en las tres dimensiones', desc: 'Evaluaste sin dejarte capturar por el halo, interviniste en el nivel estructural, y mantuviste consistencia independientemente del contexto. Las tres dimensiones apuntan en la misma dirección. Esto no es intuición — es un sistema. La pregunta que queda abierta: ¿ese sistema puede operar sin ti?', action: 'Esta semana, documenta una decisión importante que tomaste recientemente: el proceso, los criterios, las alternativas descartadas. El objetivo no es archivarla — es que alguien de tu equipo pueda replicar ese nivel de análisis sin necesitarte.' },
};

const R1_OPTS = {
  A: { letter: 'A', strong: 'Apruebo. Que arranque.', text: 'El comité ya está alineado, Valentina tiene el track record para respaldar esto y frenar ahora tiene un costo de momentum que no vale la pena pagar.' },
  B: { letter: 'B', strong: 'Apruebo la fase de diseño, no el presupuesto completo.', text: 'Que avancen con la planificación detallada y vuelvan con los números validados antes de comprometer la inversión total.' },
  C: { letter: 'C', strong: 'Pido que el comité revise los supuestos antes de votar.', text: 'El ROI sin benchmark y el plan de adopción sin ajuste a nuestra madurez digital son dos variables que pueden hundir el proyecto en ejecución.' },
};

const R2_OPTS = {
  A: { letter: 'A', strong: 'Refuerzo y optimizo la cobertura este mes.', text: 'Extiendo la ventana a 15 días y ajusto turnos con los datos del trimestre anterior. El equipo sabe ejecutarlo, el resultado es medible en 30 días — y el pico de este mes queda cubierto.' },
  B: { letter: 'B', strong: 'Rediseño el protocolo de atención sin aumentar personal.', text: 'Si el problema es eficiencia, no capacidad, podemos absorber el pico con el mismo equipo. Toma 2 semanas implementarlo — llegaremos justos al inicio del pico, pero sin costo fijo adicional.' },
  C: { letter: 'C', strong: 'Diagnostico la causa este mes y acepto el pico sin solución.', text: 'La investigación tomará 3 a 4 semanas. Este mes el equipo enfrenta el pico sin refuerzo ni protocolo nuevo. Si encontramos la causa, el próximo trimestre se rompe el ciclo. Si no, habremos perdido otro mes.' },
};

const RESULT_TITLES = {
  'S2-Estructura-consistente':        'Tres dimensiones alineadas.',
  'S2-Estructura-ruido':              'El análisis es sólido. El proceso, no tanto.',
  'S2partial-Estructura-consistente': 'Arquitectura deliberada en construcción.',
  'S1-Estructura-consistente':        'Piensas sistémico. El halo te capturó igual.',
  'S1-Evento-ruido':                  'El Sistema 1 y el contexto tomaron el control.',
  'S1-Evento-consistente':            'Predecible en superficie.',
};

const SYS_LABEL   = { S1: 'Sistema 1', S2partial: 'S2 con fricción', S2: 'Sistema 2' };
const NOISE_LABEL = { consistente: 'Consistente', ruido: 'Variable' };

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

/* ─── SHARED STYLE HELPERS ──────────────────────────────────────── */
const s = {
  btnPrimary: {
    background: C.teal, color: C.navy, border: 'none', borderRadius: 8,
    padding: '14px 28px', fontSize: 14, fontWeight: 800, cursor: 'pointer',
    letterSpacing: '0.02em', display: 'inline-block',
  },
  btnSecondary: {
    background: 'transparent', color: C.teal, border: `1.5px solid ${C.teal}`,
    borderRadius: 8, padding: '12px 22px', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', display: 'inline-block',
  },
  card: {
    background: C.navyMid, border: `1px solid ${C.border}`,
    borderRadius: 12, padding: 22, marginBottom: 24,
  },
  ctxTag: {
    display: 'inline-block', background: C.redPale,
    border: '1px solid rgba(232,52,78,0.3)', borderRadius: 6,
    padding: '3px 10px', fontSize: 11, fontWeight: 700, color: C.red,
    marginBottom: 14, letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  p: { fontSize: 14, lineHeight: 1.75, color: C.body },
};

/* ─── COMPONENT ──────────────────────────────────────────────────── */
export default function SimuladorDecision() {
  const [screen,    setScreen]    = useState('intro');
  const [r1Opts,    setR1Opts]    = useState(['A', 'B', 'C']);
  const [r2Opts,    setR2Opts]    = useState(['A', 'B', 'C']);
  const [r1Choice,  setR1Choice]  = useState(null);
  const [r2Choice,  setR2Choice]  = useState(null);
  const [r3aChoice, setR3aChoice] = useState(null);
  const [r3bChoice, setR3bChoice] = useState(null);
  const [r1System,  setR1System]  = useState(null);
  const [r2Level,   setR2Level]   = useState(null);
  const [r3Noise,   setR3Noise]   = useState(null);
  const [r3bOn,     setR3bOn]     = useState(false);
  const [copied,    setCopied]    = useState(false);

  const goTo = (sc) => { setScreen(sc); window.scrollTo(0, 0); };

  const start = () => {
    setR1Opts(shuffle(['A', 'B', 'C']));
    setR2Opts(shuffle(['A', 'B', 'C']));
    goTo('r1');
  };

  const restart = () => {
    setScreen('intro');
    setR1Choice(null); setR2Choice(null); setR3aChoice(null); setR3bChoice(null);
    setR1System(null); setR2Level(null); setR3Noise(null); setR3bOn(false);
    window.scrollTo(0, 0);
  };

  const handleR1  = (k) => { if (r1Choice)  return; setR1Choice(k);  setR1System(FEEDBACK.r1[k].system); };
  const handleR2  = (k) => { if (r2Choice)  return; setR2Choice(k);  setR2Level(FEEDBACK.r2[k].level);  };
  const handleR3a = (v) => { if (r3aChoice) return; setR3aChoice(v); setTimeout(() => setR3bOn(true), 600); };
  const handleR3b = (v) => { if (r3bChoice) return; setR3bChoice(v); setR3Noise(r3aChoice !== v ? 'ruido' : 'consistente'); };

  const profileKey = `${r1System || 'S1'}-${r2Level || 'Evento'}-${r3Noise || 'ruido'}`;
  const profile    = PROFILES[profileKey] || PROFILES['S1-Evento-ruido'];

  const r3fb = r3bChoice ? (() => {
    const changed = r3aChoice !== r3bChoice;
    return {
      tag:    changed ? 'Ruido — Variabilidad detectada' : 'Ruido — Consistencia verificada',
      tagRed: changed,
      title:  changed
        ? 'La misma propuesta produjo decisiones distintas. Eso es ruido, no criterio.'
        : 'Misma decisión, contexto diferente. Tu criterio no dependió del momento.',
      body: changed
        ? 'Kahneman define el ruido como variabilidad indeseable en juicios que deberían ser consistentes. No es sesgo —que empuja siempre en la misma dirección— sino inconsistencia: el mismo caso, el mismo decisor, resultados distintos según el momento. La fatiga acumulada, la carga cognitiva y el horario produjeron dos decisiones diferentes con información idéntica. Eso no dice nada sobre tu capacidad — dice que tu proceso de decisión es sensible a condiciones que no deberían importar.'
        : 'Kahneman, Sibony y Sunstein documentaron que el "ruido de ocasión" —variabilidad producida por el estado del decisor, no por la información— contamina decisiones que deberían ser independientes del contexto. Con la misma propuesta en dos momentos de carga cognitiva muy distinta, tu decisión no cambió. Eso no es trivial: sugiere que separas el estado del criterio, o que tienes algún mecanismo —consciente o no— que estabiliza tu juicio.',
      q: changed
        ? '¿Cuántas decisiones importantes de esta semana tomaste en condiciones de alta fatiga que deberías haber diferido?'
        : '¿Ese mecanismo es explícito? ¿Funciona cuando la presión es más alta que en este escenario?',
    };
  })() : null;

  const progStep = { intro: 0, r1: 1, r2: 2, r3: 3, result: 3 }[screen] || 0;

  const handleShare = () => {
    const url  = 'https://edutechpulselab.com/recursos/simulador-decision';
    const text = `Acabo de descubrir mi perfil decisional: "${profile.name}". ¿Cuál es el tuyo? Simulador de decisión ejecutiva by Edutech Pulse Lab`;
    if (navigator.share) {
      navigator.share({ title: 'Simulador de Decisión — EPL', text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url)
        .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
        .catch(() => {});
    }
  };

  /* ── JSX helpers (render functions, not components) ──────────── */

  const ProgBar = () => (
    <div style={{ display: 'flex', gap: 6, marginBottom: 32 }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{
          flex: 1, height: 3, borderRadius: 2, transition: 'background .4s',
          background: progStep > i ? C.tealDk : progStep === i ? C.teal : C.navyLt,
        }} />
      ))}
    </div>
  );

  const RoundBadge = ({ n }) => (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: C.tealPale, border: `1px solid ${C.border}`,
      borderRadius: 20, padding: '5px 14px', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: C.teal, marginBottom: 20,
    }}>
      Ronda {n} de 3
    </div>
  );

  const ScenTitle = ({ title, sub }) => (
    <>
      <div style={{ fontSize: 'clamp(20px,5vw,28px)', fontWeight: 900, lineHeight: 1.1, marginBottom: 8, color: C.white }}>{title}</div>
      <div style={{ fontSize: 13, color: C.teal, fontWeight: 600, marginBottom: 24, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{sub}</div>
    </>
  );

  const Card = ({ tag, children }) => (
    <div style={s.card}>
      <span style={s.ctxTag}>{tag}</span>
      {children}
    </div>
  );

  const P = ({ mt, children }) => (
    <p style={{ ...s.p, ...(mt ? { marginTop: 12 } : {}) }}>{children}</p>
  );

  const Prompt = ({ text }) => (
    <div style={{ fontSize: 14, fontWeight: 700, color: C.white, marginBottom: 14 }}>{text}</div>
  );

  const OptBtn = ({ letter, strong, text, selected, disabled, onClick }) => (
    <button onClick={onClick} disabled={disabled} style={{
      background: selected ? C.tealPale : C.navyLt,
      border: `1.5px solid ${selected ? C.teal : C.border}`,
      borderRadius: 10, padding: '16px 18px', textAlign: 'left',
      cursor: disabled ? 'default' : 'pointer',
      display: 'flex', alignItems: 'flex-start', gap: 12,
      opacity: disabled && !selected ? 0.4 : 1,
      width: '100%', transition: 'border-color .2s, background .2s',
    }}>
      <div style={{
        width: 26, height: 26, border: `1.5px solid ${C.teal}`, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 800, color: C.teal, flexShrink: 0,
      }}>{letter}</div>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: C.body, textAlign: 'left' }}>
        <strong style={{ color: C.white, display: 'block', marginBottom: 2, fontSize: 13 }}>{strong}</strong>
        {text}
      </div>
    </button>
  );

  const OptsGroup = ({ opts, data, choice, onSelect }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
      {opts.map(k => (
        <OptBtn key={k} {...data[k]} selected={choice === k} disabled={!!choice} onClick={() => onSelect(k)} />
      ))}
    </div>
  );

  const FbCard = ({ fb, onNext, nextLabel }) => !fb ? null : (
    <div style={{
      background: C.navyMid, border: `1.5px solid ${C.teal}`, borderRadius: 12,
      padding: 24, marginBottom: 20,
    }}>
      <div style={{ marginBottom: 16 }}>
        <span style={{
          display: 'inline-block', borderRadius: 6, padding: '4px 12px',
          fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
          background: fb.tagRed ? C.red : C.teal,
          color: fb.tagRed ? '#fff' : C.navy,
        }}>{fb.tag}</span>
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 10, color: C.white }}>{fb.title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.7, color: C.mid, marginBottom: 16 }}>{fb.body}</div>
      <div style={{
        background: C.tealPale, borderLeft: `3px solid ${C.teal}`,
        borderRadius: '0 8px 8px 0', padding: '14px 16px',
        fontSize: 13, fontStyle: 'italic', color: C.teal, lineHeight: 1.6, marginBottom: 20,
      }}>→ {fb.q}</div>
      <button onClick={onNext} style={s.btnPrimary}>{nextLabel}</button>
    </div>
  );

  /* ── Screens ─────────────────────────────────────────────────── */

  const renderIntro = () => (
    <>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.teal, marginBottom: 12 }}>
        7AM Library · Decision Lab
      </div>
      <h1 style={{ fontSize: 'clamp(28px,7vw,42px)', fontWeight: 900, lineHeight: 1.05, marginBottom: 16, color: C.white }}>
        Tres decisiones.<br />Sin respuestas <span style={{ color: C.teal }}>correctas</span>.
      </h1>
      <p style={{ fontSize: 15, lineHeight: 1.7, color: C.mid, marginBottom: 28 }}>
        La 7AM Library destila 10 libros clave sobre decisión ejecutiva en experiencias que entrenan criterio — no solo conocimiento.<br /><br />
        Este simulador toma tres de esos fenómenos y te los hace sentir desde adentro. No hay trampa. No hay respuesta obvia. Solo tú y la decisión.
      </p>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 20, marginBottom: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[
          'Tres escenarios ejecutivos reales. Elige lo que harías, no lo que suena mejor.',
          'Feedback con base teórica después de cada decisión.',
          'Un perfil de tu patrón decisional en tres dimensiones.',
        ].map((t, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: C.mid }}>
            <div style={{
              width: 22, height: 22, background: C.tealPale, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, flexShrink: 0, color: C.teal, marginTop: 1,
            }}>→</div>
            <span>{t}</span>
          </div>
        ))}
      </div>
      <button onClick={start} style={s.btnPrimary}>Comenzar simulación →</button>
      <p style={{ fontSize: 12, color: C.muted, marginTop: 16, textAlign: 'center' }}>
        Basado en Kahneman · Meadows · Sibony &amp; Sunstein
      </p>
    </>
  );

  const renderR1 = () => (
    <>
      <RoundBadge n={1} />
      <ScenTitle title="La propuesta de Valentina" sub="Aprobación de iniciativa" />
      <Card tag="Contexto">
        <P>Valentina lidera proyectos de transformación en tu empresa. Su último proyecto —una migración crítica— se entregó antes del plazo y con presupuesto intacto. El equipo la describe como "alguien que siempre cumple".</P>
        <P mt>Hoy presenta una propuesta para implementar un nuevo sistema de analítica de datos. El documento es bien estructurado, la narrativa es clara y el pitch fue fluido. Sin embargo, las proyecciones de ROI no tienen benchmark contra industria y el plan de adopción asume un nivel de madurez digital que tu organización todavía no tiene.</P>
        <P mt>El comité espera tu voto. Los demás ya asintieron.</P>
      </Card>
      <Prompt text="¿Cuál es tu decisión?" />
      <OptsGroup opts={r1Opts} data={R1_OPTS} choice={r1Choice} onSelect={handleR1} />
      <FbCard fb={r1Choice ? FEEDBACK.r1[r1Choice] : null} onNext={() => goTo('r2')} nextLabel="Siguiente escenario →" />
    </>
  );

  const renderR2 = () => (
    <>
      <RoundBadge n={2} />
      <ScenTitle title="La misma crisis, otra vez" sub="Gestión de problema recurrente" />
      <Card tag="Contexto">
        <P>Por tercer trimestre consecutivo, el área de atención al cliente reporta un pico de reclamos en los primeros 10 días del mes. El equipo ya implementó refuerzos de personal en ese período y la métrica de resolución mejoró temporalmente, pero el patrón vuelve cada mes.</P>
        <P mt>Tu gerente de operaciones presenta tres opciones para este trimestre. El equipo está cansado del ciclo y tú tienes 48 horas para decidir.</P>
      </Card>
      <Prompt text="¿Cómo respondes?" />
      <OptsGroup opts={r2Opts} data={R2_OPTS} choice={r2Choice} onSelect={handleR2} />
      <FbCard fb={r2Choice ? FEEDBACK.r2[r2Choice] : null} onNext={() => goTo('r3')} nextLabel="Siguiente escenario →" />
    </>
  );

  const renderR3 = () => (
    <>
      <RoundBadge n={3} />
      <ScenTitle title="El mismo expediente, dos momentos" sub="Consistencia decisional" />
      <Card tag="Escenario A — Martes 9:00 a.m.">
        <P>Revisas la solicitud de presupuesto del área de innovación: S/ 85,000 para un piloto de automatización de procesos internos. El documento es sólido, el caso de negocio tiene tres escenarios bien definidos y el equipo tiene experiencia previa en proyectos similares.</P>
        <P mt>¿Apruebas?</P>
      </Card>
      <Prompt text="Decisión — Escenario A:" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        <OptBtn letter="✓" strong="Lo apruebo ahora." text="El caso es sólido y el equipo tiene experiencia. No hay razón para diferirlo." selected={r3aChoice === 'SI'} disabled={!!r3aChoice} onClick={() => handleR3a('SI')} />
        <OptBtn letter="→" strong="Lo paso a revisión esta semana." text="Quiero revisarlo con más calma antes de firmar. S/ 85,000 merece más que una lectura rápida." selected={r3aChoice === 'NO'} disabled={!!r3aChoice} onClick={() => handleR3a('NO')} />
      </div>

      {r3bOn && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: C.tealPale, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 12,
            fontSize: 12, fontWeight: 700, color: C.teal,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            ● Nuevo escenario — misma decisión, otro contexto
          </div>
          <Card tag="Escenario B — Viernes 6:15 p.m.">
            <P>La misma solicitud de presupuesto llega a tu bandeja. Mismos S/ 85,000. Mismo documento. Mismo equipo. Mismo caso de negocio.</P>
            <P mt>Pero llevas una semana intensa: dos urgencias simultáneas, una reunión que se extendió hasta las 5:30 p.m. y una conversación difícil que todavía no cerraste.</P>
            <P mt>¿Apruebas?</P>
          </Card>
          <Prompt text="Decisión — Escenario B:" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            <OptBtn letter="✓" strong="Lo apruebo." text="Ya lo leí antes. No cambió nada en la propuesta." selected={r3bChoice === 'SI'} disabled={!!r3bChoice} onClick={() => handleR3b('SI')} />
            <OptBtn letter="→" strong="Lo dejo para el lunes." text="Ha sido una semana larga. Prefiero tener la cabeza fría antes de comprometer ese monto." selected={r3bChoice === 'NO'} disabled={!!r3bChoice} onClick={() => handleR3b('NO')} />
          </div>
          <FbCard fb={r3fb} onNext={() => goTo('result')} nextLabel="Ver mi perfil →" />
        </>
      )}
    </>
  );

  const renderResult = () => (
    <>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.teal, marginBottom: 12 }}>
        Tu perfil decisional
      </div>
      <h1 style={{ fontSize: 'clamp(22px,5vw,36px)', fontWeight: 900, lineHeight: 1.05, marginBottom: 8, color: C.white }}>
        {RESULT_TITLES[profileKey] || 'Tu perfil decisional.'}
      </h1>
      <p style={{ fontSize: 14, color: C.muted, marginBottom: 28 }}>Tres dimensiones independientes. Un solo perfil.</p>

      {/* Metrics grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 28 }}>
        {[
          { label: 'Evaluación',   value: SYS_LABEL[r1System]   || '—', sub: 'Sistema 1 / 2'   },
          { label: 'Intervención', value: r2Level                || '—', sub: 'nivel sistémico'  },
          { label: 'Consistencia', value: NOISE_LABEL[r3Noise]   || '—', sub: 'ruido decisional' },
        ].map(({ label, value, sub }) => (
          <div key={label} style={{
            background: C.navyMid, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: '16px 8px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 900, color: C.teal, paddingTop: 4, wordBreak: 'break-word' }}>{value}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Profile card */}
      <div style={{ background: C.navyMid, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Perfil identificado</div>
        <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 10, color: C.white }}>{profile.name}</div>
        <div style={{ fontSize: 14, lineHeight: 1.7, color: C.mid }}>{profile.desc}</div>
      </div>

      {/* Dimensions summary */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
        {[
          { name: 'Evaluación — Sistema 1 / Sistema 2', value: SYS_LABEL[r1System]   || '—', hi: r1System === 'S1'      },
          { name: 'Intervención — Nivel sistémico',     value: r2Level               || '—', hi: r2Level === 'Evento'    },
          { name: 'Consistencia — Ruido decisional',    value: NOISE_LABEL[r3Noise]  || '—', hi: r3Noise === 'ruido'    },
        ].map(({ name, value, hi }) => (
          <div key={name} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: C.navyMid, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: '12px 16px',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.white }}>{name}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: hi ? C.red : C.teal, marginLeft: 12, flexShrink: 0 }}>{value}</span>
          </div>
        ))}
      </div>

      {/* Action card */}
      <div style={{
        background: 'linear-gradient(135deg,rgba(42,191,191,0.08),rgba(42,191,191,0.03))',
        border: `1.5px solid ${C.teal}`, borderRadius: 12, padding: 20, marginBottom: 20,
      }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>→ Acción de esta semana</div>
        <div style={{ fontSize: 14, lineHeight: 1.7, color: C.white, fontStyle: 'italic' }}>{profile.action}</div>
      </div>

      {/* CTA block */}
      <div style={{
        background: `linear-gradient(135deg,${C.tealPale},transparent)`,
        border: `1px solid ${C.border}`, borderRadius: 12,
        padding: 22, textAlign: 'center', marginBottom: 16,
      }}>
        <p style={{ fontSize: 12, color: C.teal, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
          7AM Library · Edutech Pulse Lab
        </p>
        <strong style={{ fontSize: 16, lineHeight: 1.4, display: 'block', marginBottom: 8, color: C.white }}>
          Este simulador destila los episodios 0, 1 y 2.<br />Hay 8 más.
        </strong>
        <p style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
          Cada episodio convierte un libro clave sobre decisión ejecutiva en una experiencia que entrena criterio — no solo conocimiento.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="https://edutechpulselab.com/7am-library" style={{ textDecoration: 'none' }}>
            <button style={s.btnPrimary}>Ver los 10 episodios →</button>
          </a>
          <button onClick={handleShare} style={s.btnSecondary}>
            {copied ? '✓ Link copiado' : 'Compartir ↗'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <button onClick={restart} style={{ ...s.btnSecondary, fontSize: 13, padding: '10px 20px' }}>
          Reiniciar simulación
        </button>
      </div>

      <div style={{
        textAlign: 'center', paddingTop: 24, borderTop: `1px solid ${C.border}`,
        fontSize: 12, color: C.muted, fontWeight: 600, letterSpacing: '0.05em',
      }}>
        <span style={{ color: C.teal }}>7AM Library</span> · by Edutech Pulse Lab
      </div>
    </>
  );

  const screens = { intro: renderIntro, r1: renderR1, r2: renderR2, r3: renderR3, result: renderResult };
  const renderScreen = screens[screen] || renderIntro;

  /* ── RENDER ──────────────────────────────────────────────────── */
  return (
    <>
      <SEOHead
        title="Simulador de Decisión Ejecutiva — 3 escenarios, 18 perfiles | Edutech Pulse Lab"
        description="Descubre tu perfil decisional en 3 escenarios ejecutivos reales. Sin respuestas correctas: mide Sistema 1 vs 2, nivel de intervención sistémica y ruido decisional. Gratis, sin registro."
        keywords="simulador decisión ejecutiva, sesgos cognitivos, sistema 1 sistema 2, kahneman, pensamiento sistémico, ruido decisional, perfil decisional, herramientas liderazgo gratis, decision lab"
        canonicalUrl="/recursos/simulador-decision"
        ogType="website"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Simulador de Decisión Ejecutiva',
          applicationCategory: 'EducationalApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          description: '3 escenarios ejecutivos para descubrir tu perfil decisional. Mide Sistema 1 vs 2, nivel de intervención sistémica y ruido decisional.',
          author: { '@type': 'Organization', name: 'Edutech Pulse Lab', url: 'https://edutechpulselab.com' },
        }}
      />

      <Navbar />

      {/* Dark simulator shell */}
      <div style={{
        background: C.navy,
        minHeight: 'calc(100vh - 64px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '20px 16px 56px',
      }}>
        <div style={{ width: '100%', maxWidth: 680, fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>

          {/* Back button — integrado en el tema oscuro */}
          <div style={{ marginBottom: 24 }}>
            <Link to="/recursos" style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'rgba(42,191,191,0.08)',
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '7px 14px',
              fontSize: 13, fontWeight: 600,
              color: C.muted,
              textDecoration: 'none',
              transition: 'background .2s, color .2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = C.tealPale; e.currentTarget.style.color = C.teal; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(42,191,191,0.08)'; e.currentTarget.style.color = C.muted; }}
            >
              <ArrowLeft style={{ width: 14, height: 14 }} />
              Recursos
            </Link>
          </div>

          {/* Progress bar — solo en rondas activas */}
          {screen !== 'intro' && screen !== 'result' && <ProgBar />}

          {/* Screen content */}
          {renderScreen()}

        </div>
      </div>
    </>
  );
}
